import {
  MatchCardShell,
  MatchStubBadge,
  MatchStubDeltaPill,
  MatchStubEventName,
  MatchStubFooter,
  MatchStubHeader,
} from './MatchStubShell'

export type AdjustmentMatchInput = {
  eventFormat?: string
  /** Free-text metadata map. ADJUSTMENT matches carry the admin `adminReason` here. */
  extras?: Record<string, string> | null
  teams?: Array<{
    player1?: {
      postMatchRating?: {
        singles?: number | string
        doubles?: number | string
      }
    }
    preMatchRatingAndImpact?: {
      matchSingleRatingImpactPlayer1?: number
      matchDoubleRatingImpactPlayer1?: number
    }
  }>
}

type Props = {
  matchDate?: string
  eventName?: string
  match?: AdjustmentMatchInput | null
}

type RatingChange = { before: number; after: number; delta: number }

function computeRatingChange(match: AdjustmentMatchInput | null | undefined): RatingChange | null {
  if (!match) return null
  const team0 = match.teams?.[0]
  if (!team0) return null

  const isSingles = match.eventFormat === 'SINGLES'
  const ratingKey = isSingles ? 'singles' : 'doubles'
  const impactKey = isSingles ? 'matchSingleRatingImpactPlayer1' : 'matchDoubleRatingImpactPlayer1'

  const after = Number(team0.player1?.postMatchRating?.[ratingKey])
  const impact = team0.preMatchRatingAndImpact?.[impactKey]

  if (!Number.isFinite(after) || impact == null) return null

  return { before: after - impact, after, delta: impact }
}

export function AdjustmentMatchStub({ matchDate, eventName, match }: Props) {
  const rating = computeRatingChange(match)
  const reason = match?.extras?.adminReason?.trim() || null

  return (
    <MatchCardShell>
      <MatchStubHeader>
        <MatchStubBadge label="ADJUSTMENT" variant="info" />
        {rating ? <MatchStubDeltaPill delta={rating.delta} /> : <span />}
      </MatchStubHeader>
      <MatchStubEventName name={eventName} />
      {rating && (
        <div className="mt-3 flex justify-center items-center gap-2 text-sm text-navy-900">
          <span>{rating.before.toFixed(3)}</span>
          <span className="text-navy-900 text-opacity-40">→</span>
          <span className="font-semibold">{rating.after.toFixed(3)}</span>
        </div>
      )}
      {reason && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs font-medium text-navy-900 text-opacity-60 mb-1">Reason</p>
          <p className="text-sm text-navy-900">{reason}</p>
        </div>
      )}
      <MatchStubFooter matchDate={matchDate} />
    </MatchCardShell>
  )
}
